import { extension as getExtensionFromMimeType } from 'mime-types';
import { i18n } from '@/shared/i18n';

interface FileToDownload {
  /** Remote or pre-built object URL to download. Mutually exclusive with `blob`. */
  url?: string;
  /** In-memory Blob to download; its object URL is created and revoked internally. */
  blob?: Blob;
  mimeType?: string;
  fileName?: string;
}

const ENCODER = new TextEncoder();

/** Leaves room for a caller's suffix and extension inside a 255-byte filename. */
const MAX_FILENAME_BYTES = 200;

/**
 * A name a filesystem will accept, keeping everything it has no objection to.
 * Single source of truth for every download sink in the app.
 *
 * Strips the reserved set rather than allow-listing ASCII letters. The allow-list
 * version dropped every accent, every CJK character and every parenthesis — so a
 * Japanese title fell through to `fallback` entirely and `Q4 (draft)` arrived as
 * `Q4 draft`. This names a `blob:` download, where `a.download` is the only thing
 * naming the file, so whatever it removes is removed from what the user receives.
 *
 * Three things it must remove even though a filesystem would take them:
 *   - BIDI controls. `U+202E` before `moc.exe` renders as `exe.com`, so a title
 *     someone else authored could offer a download that reads as an executable.
 *     `\p{Bidi_Control}` rather than a hand-written range, which missed `U+061C`.
 *   - a leading dot, which hides the file in Finder and most Linux managers.
 *   - the byte length, counted in BYTES over whole code points: CJK is three
 *     bytes per UTF-16 unit, and slicing by unit can also split an emoji and
 *     leave a lone surrogate that `encodeURIComponent` refuses.
 */
// eslint-disable-next-line i18next/no-literal-string -- a download filename slug, not copy
export const sanitizeDownloadFileName = (name: string, fallback = 'file'): string => {
  const stripped = name
    // eslint-disable-next-line no-control-regex -- control and bidi characters are exactly what this removes
    .replace(/(?:[\u0000-\u001f\u007f-\u009f<>:"/\\|?*]|\p{Bidi_Control})+/gu, ' ')
    .replace(/\s+/g, ' ')
    /*
     * Dots AND spaces together, not a contiguous run of dots. `^\.+` left
     * `. .config` as `.config`, still hidden, because the second dot is not at
     * position 0.
     */
    .replace(/^[\s.]+/, '')
    // The reserved-character replacement leaves a trailing space on
    // `Q4/Q1: "EMEA" <draft>`, and the expression above only strips from the start.
    .trim();

  let bytes = 0;
  let out = '';
  for (const char of stripped) {
    const width = ENCODER.encode(char).length;
    if (bytes + width > MAX_FILENAME_BYTES) {
      break;
    }
    bytes += width;
    out += char;
  }

  /*
   * The byte cut lands wherever the budget runs out, which can be immediately
   * after a space - and a trailing dot was never stripped at all, since the
   * expression above only strips from the START and `.trim()` only removes
   * whitespace. Win32 silently drops both during path normalisation, so the file
   * arrives under a name the app never offered rather than being rejected, which
   * is the harder failure to notice.
   */
  out = out.replace(/[\s.]+$/, '');

  // A name of nothing but dots is a filesystem trap, not a name.
  return /[^.\s]/.test(out) ? out : fallback;
};
export const downloadFile = async ({ url, blob, mimeType, fileName }: FileToDownload) => {
  let downloadUrl: string;
  let shouldRevoke = false;

  if (blob) {
    // Caller supplied the bytes directly (e.g. a generated Markdown/PDF blob).
    downloadUrl = window.URL.createObjectURL(blob);
    shouldRevoke = true;
  } else if (url?.startsWith('blob:')) {
    // Already an object URL — use it directly.
    downloadUrl = url;
  } else if (url) {
    // Fetch the resource and create a blob URL.
    const resp = await fetch(url);
    if (!resp.ok)
      throw new Error(
        i18n.t('common:utils.downloadFile.fetchFailed', {
          status: resp.status,
          statusText: resp.statusText,
        }),
      );
    const fetched = await resp.blob();
    downloadUrl = window.URL.createObjectURL(fetched);
    shouldRevoke = true;
  } else {
    throw new Error('downloadFile requires either a `url` or a `blob`.');
  }

  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const a = document.createElement('a');
  // eslint-disable-next-line i18next/no-literal-string -- file extension fallback
  const ext = mimeType ? getExtensionFromMimeType(mimeType) || 'png' : 'png';
  a.href = downloadUrl;
  a.download = fileName || `file.${ext}`;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  // Only revoke the object URL if we created it
  if (shouldRevoke) {
    window.URL.revokeObjectURL(downloadUrl);
  }
};
